import {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import {
  SAVED_VIEW_COPY,
  SAVED_VIEW_MARKER_KEY,
  SAVED_VIEW_VALIDATION_MESSAGES,
  buildSavedViewQueryUpdates,
  canonicalizeFilters,
  containsAstral,
  isViewModified,
  pickSaveableParams,
  missingReferencesCopy,
  validateSavedViewName,
} from './savedViewFilters';

// ENG-3551: the effectful half of saved views for one mounted page — list load,
// selection recovery, apply/revert, and the four mutations. Everything is inert
// while `enabled` is false: no requests, no notices, no URL or sticky writes.
//
// Rows are never fetched here. Apply hands one update object to the filter bar's
// existing `updateQuery` (the same gesture path as any filter click); selection
// changes that keep the filters (create, delete, invalid marker) go through
// `replaceMarker`, a history.replace the records HOC does not refetch on.

export const SAVED_VIEW_ANALYTICS_EVENT = 'saved_view_interaction';

const INERT_LIST = Object.freeze({
  views: Object.freeze([]), loaded: false, loading: false, error: null,
});

// A malformed marker (array/object from a hand-edited URL) is still a marker:
// it matches no view, so selection recovery removes it.
const markerFrom = (params) => {
  const value = params?.[SAVED_VIEW_MARKER_KEY];
  if (value === undefined || value === null || value === '') return null;
  return typeof value === 'string' ? value : JSON.stringify(value);
};

const statusOf = (result) => result?.response?.status ?? null;

// Map a failed mutation to what the user sees. 422 bodies are the server's exact
// validation copy (only known strings are shown); 400 and transport failures get
// the generic copy; 403 hides the feature; 404 triggers selection recovery.
export const classifyMutationFailure = (result, fallback) => {
  const status = statusOf(result);
  if (status === 403) return { kind: 'access', message: SAVED_VIEW_COPY.accessDenied };
  if (status === 404) return { kind: 'not_found', message: SAVED_VIEW_COPY.unavailable };
  if (status === 422) {
    const message = result?.response?.data?.error;
    return {
      kind: 'validation',
      message: SAVED_VIEW_VALIDATION_MESSAGES.includes(message) ? message : fallback,
    };
  }
  return { kind: 'transport', message: fallback };
};

const teamValues = (filters) => {
  const team = filters?.team;
  if (team === undefined || team === null || team === '') return [];
  return (Array.isArray(team) ? team : [team]).map(String);
};

// Notices about an apply describe the URL the user was on; a later URL change
// makes their Retry (or "your current filters have not changed") stale.
const APPLY_NOTICE_KINDS = ['missing', 'lookup_failed'];

const failedResult = (result) => Boolean(result && (result.error || result.canceled));

export const useSavedViews = ({
  page,
  orgId,
  userId,
  params,
  updateQuery,
  replaceMarker,
  options,
  storageKey,
  list,
  fetchSavedViews,
  createSavedView,
  updateSavedView,
  deleteSavedView,
  onInteraction,
  enabled: enabledProp,
}) => {
  // Lists are keyed by user and acting org; without both identities nothing
  // could be stored or shown, so the feature stays inert.
  const enabled = enabledProp === true && Boolean(userId) && orgId !== null && orgId !== undefined;
  const context = useMemo(() => ({
    userId, orgId, page, enabled,
  }), [userId, orgId, page, enabled]);
  const identity = `${userId}|${orgId}|${page}|${enabled}`;
  const currentList = enabled ? (list || INERT_LIST) : INERT_LIST;

  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [accessDeniedLocal, setAccessDeniedLocal] = useState(false);
  // A list left in Redux by an earlier mount can predate a view created in another
  // session; only a list fetched by THIS mount may remove a marker.
  const [freshListSettled, setFreshListSettled] = useState(false);
  const [dismissedListError, setDismissedListError] = useState(null);

  const identityRef = useRef(identity);
  const paramsRef = useRef(params);
  const applySeqRef = useRef(0);
  const mountedRef = useRef(true);
  const recoveredMarkerRef = useRef(null);
  const recheckedMarkerRef = useRef(null);
  const optionsRef = useRef(options);
  const latest = useRef({});
  const accessDenied = enabled === true && (accessDeniedLocal || currentList.error?.status === 403);
  const active = enabled === true && !accessDenied;
  const activeRef = useRef(active);
  const listLoadedRef = useRef(currentList.loaded);

  // Latest-value refs for async completions and callbacks. Synced in a layout
  // effect (not during render) so every passive effect below already sees them.
  useLayoutEffect(() => {
    identityRef.current = identity;
    paramsRef.current = params;
    optionsRef.current = options;
    activeRef.current = active;
    listLoadedRef.current = currentList.loaded;
    latest.current = {
      updateQuery, replaceMarker, fetchSavedViews, createSavedView, updateSavedView, deleteSavedView, onInteraction,
    };
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      applySeqRef.current += 1;
    };
  }, []);

  const cancelPendingApply = useCallback(() => {
    applySeqRef.current += 1;
    optionsRef.current?.reset?.();
  }, []);

  const track = useCallback((action, extra) => {
    latest.current.onInteraction?.(action, extra);
  }, []);

  const loadList = useCallback(() => {
    if (!context.enabled || !latest.current.fetchSavedViews) return Promise.resolve(null);
    const capturedIdentity = identityRef.current;
    return Promise.resolve(latest.current.fetchSavedViews(page, context))
      .catch(() => null)
      .then((result) => {
        if (mountedRef.current && capturedIdentity === identityRef.current) setFreshListSettled(true);
        return result;
      });
  }, [context, page]);

  // A successful mutation retires any in-flight list fetch (so an older reply cannot
  // undo it); a list that never finished loading must be fetched again, or it would
  // show only the views mutated this mount.
  const reloadIfUnloaded = useCallback(() => {
    if (!listLoadedRef.current) loadList();
  }, [loadList]);

  // Identity change (org switch, page, flag): reset everything and load once.
  useEffect(() => {
    setNotice(null);
    setSaving(false);
    setAccessDeniedLocal(false);
    setFreshListSettled(false);
    setDismissedListError(null);
    recoveredMarkerRef.current = null;
    recheckedMarkerRef.current = null;
    cancelPendingApply();
    if (context.enabled) loadList();
  }, [context, loadList, cancelPendingApply]);

  // Any URL change while an apply is still validating cancels that apply: the
  // user has moved on, and the apply must not overwrite what they just chose.
  const searchSignature = useMemo(() => JSON.stringify(params || {}), [params]);
  useEffect(() => {
    cancelPendingApply();
    setNotice((current) => (current && APPLY_NOTICE_KINDS.includes(current.kind) ? null : current));
  }, [searchSignature, cancelPendingApply]);

  // A 403 hides the feature for the rest of the mount, pending apply included.
  useEffect(() => {
    if (accessDenied) cancelPendingApply();
  }, [accessDenied, cancelPendingApply]);

  const marker = active ? markerFrom(params) : null;
  // Any known row can show as selected (a just-created view before the first list
  // load, a cached list); only removal of a marker needs the authoritative list.
  const selectedView = useMemo(() => {
    if (!marker) return null;
    return currentList.views.find((view) => String(view.id) === marker) || null;
  }, [marker, currentList]);

  // Selection recovery: only a successfully loaded list for this user/org/page is
  // authoritative. A marker it does not contain (deleted, malformed, another
  // page's or another org's id) is removed by history.replace; filters stay.
  useEffect(() => {
    // A failed fetch is not proof of deletion, even with an older list cached.
    if (!active || !marker || !freshListSettled || !currentList.loaded || currentList.loading || currentList.error) return;
    if (selectedView) {
      recoveredMarkerRef.current = null;
      return;
    }
    if (recoveredMarkerRef.current === marker) return;
    // The list may predate the marker (a view created in another tab after this
    // mount's fetch): confirm with one more fetch before removing it.
    if (recheckedMarkerRef.current !== marker) {
      recheckedMarkerRef.current = marker;
      setFreshListSettled(false);
      loadList();
      return;
    }
    recoveredMarkerRef.current = marker;
    setNotice({ kind: 'unavailable', message: SAVED_VIEW_COPY.unavailable });
    latest.current.replaceMarker?.(null);
  }, [active, marker, currentList, selectedView, freshListSettled, loadList]);

  const isModified = active && selectedView ? isViewModified(selectedView, params, page) : false;

  const isCurrent = useCallback((capturedIdentity) => (
    mountedRef.current && capturedIdentity === identityRef.current
  ), []);

  // Runs one mutation with `saving` always reset, and turns a throw (e.g. a reducer
  // error inside dispatch) into an ordinary transport failure.
  const runMutation = useCallback(async (send) => {
    setSaving(true);
    try {
      return await send();
    } catch {
      return { error: true };
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, []);

  const writeSticky = useCallback((filters) => {
    if (!storageKey) return;
    const teams = teamValues(filters);
    try {
      if (teams.length > 0) localStorage.setItem(storageKey, teams.join(','));
      else localStorage.removeItem(storageKey);
    } catch { /* storage unavailable: the URL still carries the team */ }
  }, [storageKey]);

  const handleFailure = useCallback((result, fallback, retry) => {
    const failure = classifyMutationFailure(result, fallback);
    if (failure.kind === 'access') {
      setAccessDeniedLocal(true);
      setNotice({ kind: 'access', message: failure.message });
    } else if (failure.kind === 'not_found') {
      setNotice({ kind: 'unavailable', message: failure.message });
      loadList();
    } else if (failure.kind === 'transport' && retry) {
      setNotice({ kind: 'mutation_failed', message: failure.message, retry });
    }
    return failure;
  }, [loadList]);

  const apply = useCallback(async (view) => {
    if (!active || !view) return;
    applySeqRef.current += 1;
    const seq = applySeqRef.current;
    const capturedIdentity = identityRef.current;
    // The cancel effect is passive, so a lookup can settle after a URL change has
    // committed but before that effect runs; compare the committed params directly.
    const capturedParams = JSON.stringify(paramsRef.current || {});
    let result;
    try {
      result = await optionsRef.current.check(view.filters || {});
    } catch {
      result = { missing: [], unverifiable: ['lookup'], stale: false };
    }
    if (seq !== applySeqRef.current || !isCurrent(capturedIdentity) || !activeRef.current || result.stale) return;
    if (JSON.stringify(paramsRef.current || {}) !== capturedParams) return;

    if (result.missing.length > 0) {
      setNotice({ kind: 'missing', message: missingReferencesCopy(result.missing) });
      track('apply_blocked', { reason: 'missing_references' });
      return;
    }
    if (result.unverifiable.length > 0) {
      setNotice({ kind: 'lookup_failed', message: SAVED_VIEW_COPY.lookupFailed, retry: () => apply(view) });
      track('apply_blocked', { reason: 'unverifiable' });
      return;
    }

    setNotice(null);
    writeSticky(view.filters);
    latest.current.updateQuery(buildSavedViewQueryUpdates({ page, view }));
  }, [active, isCurrent, page, track, writeSticky]);

  const revert = useCallback(() => apply(selectedView), [apply, selectedView]);

  const currentCanonicalFilters = useCallback(() => {
    const { filters, error } = canonicalizeFilters(pickSaveableParams(paramsRef.current || {}, page), page);
    if (error) return { error };
    if (containsAstral(JSON.stringify(filters))) return { error: SAVED_VIEW_COPY.invalidFilter };
    return { filters };
  }, [page]);

  // Resolves { ok, error } so the Save popover can stay open on a failure.
  const saveNew = useCallback(async (rawName) => {
    if (!active) return { ok: false, error: null };
    const nameError = validateSavedViewName(rawName);
    if (nameError) return { ok: false, error: nameError };
    // Captured at submit time, never when the popover opened.
    const { filters, error } = currentCanonicalFilters();
    if (error) return { ok: false, error };

    const capturedIdentity = identityRef.current;
    // The new view is selected only on the state it was saved from: same selection,
    // same saveable filters (search, DOB, dates and page may change meanwhile).
    const selectionSignature = () => JSON.stringify([markerFrom(paramsRef.current), currentCanonicalFilters().filters]);
    const signatureAtSubmit = selectionSignature();
    const result = await runMutation(() => latest.current.createSavedView(page, String(rawName).trim(), filters, context));
    if (!isCurrent(capturedIdentity)) return { ok: false, error: null };
    if (result?.skipped) return { ok: false, error: null };
    if (failedResult(result)) {
      const failure = handleFailure(result, SAVED_VIEW_COPY.saveFailed);
      return { ok: false, error: failure.message };
    }
    const created = result?.data?.saved_inbox_view;
    // A view applied, or filters changed, while the save was in flight: keep that state.
    if (created?.id != null && selectionSignature() === signatureAtSubmit) {
      recoveredMarkerRef.current = null;
      latest.current.replaceMarker?.(String(created.id));
    }
    reloadIfUnloaded();
    return { ok: true, error: null };
  }, [active, context, currentCanonicalFilters, handleFailure, isCurrent, page, reloadIfUnloaded, runMutation]);

  // Retry re-sends the same filters to the same view: never the live URL, which
  // may by then belong to another view or to none.
  const sendUpdate = useCallback(async (targetId, filters) => {
    const capturedIdentity = identityRef.current;
    const result = await runMutation(() => latest.current.updateSavedView(targetId, { filters }, context));
    if (!isCurrent(capturedIdentity)) return;
    if (result?.skipped) return;
    if (failedResult(result)) {
      const failure = handleFailure(result, SAVED_VIEW_COPY.saveFailed, () => sendUpdate(targetId, filters));
      if (failure.kind === 'validation') setNotice({ kind: 'validation', message: failure.message });
      return;
    }
    setNotice(null);
    reloadIfUnloaded();
  }, [context, handleFailure, isCurrent, reloadIfUnloaded, runMutation]);

  const update = useCallback(async () => {
    if (!active || !selectedView) return;
    const { filters, error } = currentCanonicalFilters();
    if (error) {
      setNotice({ kind: 'validation', message: error });
      return;
    }
    await sendUpdate(selectedView.id, filters);
  }, [active, currentCanonicalFilters, selectedView, sendUpdate]);

  const rename = useCallback(async (id, rawName) => {
    if (!active) return { ok: false, error: null };
    const nameError = validateSavedViewName(rawName);
    if (nameError) return { ok: false, error: nameError };
    const capturedIdentity = identityRef.current;
    const result = await runMutation(() => latest.current.updateSavedView(id, { name: String(rawName).trim() }, context));
    if (!isCurrent(capturedIdentity)) return { ok: false, error: null };
    if (result?.skipped) return { ok: false, error: null };
    if (failedResult(result)) {
      const failure = handleFailure(result, SAVED_VIEW_COPY.saveFailed);
      return { ok: failure.kind === 'not_found', error: failure.kind === 'not_found' ? null : failure.message };
    }
    reloadIfUnloaded();
    return { ok: true, error: null };
  }, [active, context, handleFailure, isCurrent, reloadIfUnloaded, runMutation]);

  const remove = useCallback(async (id) => {
    if (!active) return { ok: false };
    const capturedIdentity = identityRef.current;
    const removesSelection = markerFrom(paramsRef.current) === String(id);
    // The success dispatch drops the view before the marker is replaced; mark it
    // handled first so recovery does not report a deliberate delete as missing.
    if (removesSelection) recoveredMarkerRef.current = String(id);
    const result = await runMutation(() => latest.current.deleteSavedView(id, context));
    if (!isCurrent(capturedIdentity)) return { ok: false };
    if (result?.skipped) {
      if (removesSelection) recoveredMarkerRef.current = null;
      return { ok: false };
    }
    // Only a marker that still names this view goes (the user may have applied
    // another view while the delete was in flight); filters and rows stay.
    const stillSelected = removesSelection && markerFrom(paramsRef.current) === String(id);
    if (failedResult(result)) {
      if (removesSelection && statusOf(result) !== 404) recoveredMarkerRef.current = null;
      handleFailure(result, SAVED_VIEW_COPY.deleteFailed, () => remove(id));
      if (stillSelected && statusOf(result) === 404) latest.current.replaceMarker?.(null);
      return { ok: false };
    }
    if (stillSelected) latest.current.replaceMarker?.(null);
    reloadIfUnloaded();
    return { ok: true };
  }, [active, context, handleFailure, isCurrent, reloadIfUnloaded, runMutation]);

  const listNotice = useMemo(() => (
    active && currentList.error && !currentList.loading && currentList.error !== dismissedListError
      ? { kind: 'list_failed', message: SAVED_VIEW_COPY.listFailed, retry: loadList }
      : null
  ), [active, currentList.error, currentList.loading, dismissedListError, loadList]);
  const visibleNotice = enabled ? (notice || listNotice) : null;

  const retry = useCallback(async () => {
    const current = notice || listNotice;
    if (!current?.retry) return;
    setNotice(null);
    await current.retry();
  }, [notice, listNotice]);

  const dismissNotice = useCallback(() => {
    if (notice) setNotice(null);
    else if (listNotice) setDismissedListError(currentList.error);
  }, [notice, listNotice, currentList.error]);

  return {
    available: active,
    views: active ? currentList.views : INERT_LIST.views,
    selectedView: active ? selectedView : null,
    isModified,
    accessDenied,
    loading: active && currentList.loading && !currentList.loaded,
    saving,
    notice: visibleNotice,
    apply,
    saveNew,
    update,
    revert,
    rename,
    remove,
    retry,
    dismissNotice,
  };
};

export default useSavedViews;
